import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Settings, Mail, Lock, Download, Trash2, Calendar, BarChart3, AlertTriangle, Eye, EyeOff } from 'lucide-react';

export default function AccountSettings() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [promoEmails, setPromoEmails] = useState(user?.consent_promotional ?? true);
  const [weeklyDigest, setWeeklyDigest] = useState(true);
  const [partnerOffers, setPartnerOffers] = useState(true);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [stats, setStats] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [changingPw, setChangingPw] = useState(false);

  useEffect(() => {
    api.get('/users/stats').then(r => setStats(r.data)).catch(() => {});
  }, []);

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await api.put('/users/settings', { consent_promotional: promoEmails, weekly_digest: weeklyDigest, partner_offers: partnerOffers });
      setUser(res.data);
      toast.success('Settings updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    setChangingPw(true);
    try {
      await api.put('/users/password', { current_password: currentPw, new_password: newPw });
      setCurrentPw(''); setNewPw('');
      toast.success('Password changed successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPw(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await api.delete('/users/account');
      toast.success('Account deleted');
      logout();
    } catch {
      toast.error('Failed to delete account');
    }
  };

  const isGoogleAuth = user?.auth_type === 'google' || !user?.auth_type;

  return (
    <div className="max-w-2xl mx-auto px-4 lg:px-8 py-8 lg:py-12" data-testid="account-settings-page">
      <div className="flex items-center gap-3 mb-8 animate-fade-in">
        <div className="w-10 h-10 rounded-xl bg-[#4d7cfe]/15 flex items-center justify-center">
          <Settings className="h-5 w-5 text-[#4d7cfe]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Account Settings</h1>
          <p className="text-[#a0a8b8] text-sm">{user?.email}</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Email Preferences */}
        <div className="ciq-card">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="h-4 w-4 text-[#4d7cfe]" />
            <h2 className="text-white font-semibold">Email Preferences</h2>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div><p className="text-sm text-white">Promotional Emails</p><p className="text-xs text-[#64748b]">Receive offers from CasinoIQ and partners</p></div>
              <Switch checked={promoEmails} onCheckedChange={setPromoEmails} className="data-[state=checked]:bg-[#4d7cfe]" data-testid="settings-promo-toggle" />
            </div>
            <div className="flex items-center justify-between">
              <div><p className="text-sm text-white">Weekly Digest</p><p className="text-xs text-[#64748b]">Weekly summary of new tools and features</p></div>
              <Switch checked={weeklyDigest} onCheckedChange={setWeeklyDigest} className="data-[state=checked]:bg-[#4d7cfe]" data-testid="settings-digest-toggle" />
            </div>
            <div className="flex items-center justify-between">
              <div><p className="text-sm text-white">Partner Offers</p><p className="text-xs text-[#64748b]">Exclusive casino partner deals</p></div>
              <Switch checked={partnerOffers} onCheckedChange={setPartnerOffers} className="data-[state=checked]:bg-[#4d7cfe]" data-testid="settings-partner-toggle" />
            </div>
          </div>
          <button onClick={handleSaveSettings} disabled={savingSettings}
            className="mt-4 h-10 px-6 rounded-xl bg-[#4d7cfe] text-white text-sm font-medium btn-lift disabled:opacity-50"
            data-testid="save-settings-btn">
            {savingSettings ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>

        {/* Change Password */}
        {!isGoogleAuth && (
          <div className="ciq-card">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="h-4 w-4 text-[#8b5cf6]" />
              <h2 className="text-white font-semibold">Change Password</h2>
            </div>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="text-sm text-[#a0a8b8] mb-1.5 block">Current Password</label>
                <div className="relative">
                  <input type={showPw ? 'text' : 'password'} value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} required
                    className="w-full h-11 px-4 pr-10 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none"
                    data-testid="settings-current-pw" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748b]">
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-sm text-[#a0a8b8] mb-1.5 block">New Password</label>
                <input type={showPw ? 'text' : 'password'} value={newPw} onChange={(e) => setNewPw(e.target.value)} required minLength={8}
                  className="w-full h-11 px-4 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none"
                  placeholder="Minimum 8 characters" data-testid="settings-new-pw" />
              </div>
              <button type="submit" disabled={changingPw}
                className="h-10 px-6 rounded-xl bg-[#8b5cf6] text-white text-sm font-medium btn-lift disabled:opacity-50"
                data-testid="change-pw-btn">
                {changingPw ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </div>
        )}

        {/* Account Stats */}
        <div className="ciq-card">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="h-4 w-4 text-[#10b981]" />
            <h2 className="text-white font-semibold">Account Stats</h2>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 rounded-lg bg-[#1a1f2e]">
              <p className="text-2xl font-bold text-white">{stats?.tools_used ?? 0}</p>
              <p className="text-xs text-[#64748b]">Tools Used</p>
            </div>
            <div className="p-3 rounded-lg bg-[#1a1f2e]">
              <p className="text-sm font-medium text-white flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-[#4d7cfe]" /> {stats?.member_since ? new Date(stats.member_since).toLocaleDateString() : 'N/A'}</p>
              <p className="text-xs text-[#64748b]">Member Since</p>
            </div>
          </div>
        </div>

        {/* Data & Account */}
        <div className="ciq-card">
          <h2 className="text-white font-semibold mb-4">Data & Account</h2>
          <div className="space-y-3">
            <button onClick={() => toast.info('Export feature coming soon!')}
              className="w-full h-10 rounded-lg border border-white/10 text-[#a0a8b8] text-sm hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
              data-testid="export-data-btn">
              <Download className="h-4 w-4" /> Export My Data
            </button>
            <button onClick={() => setShowDelete(true)}
              className="w-full h-10 rounded-lg border border-[#ef4444]/30 text-[#ef4444] text-sm hover:bg-[#ef4444]/10 transition-colors flex items-center justify-center gap-2"
              data-testid="delete-account-btn">
              <Trash2 className="h-4 w-4" /> Delete Account
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent className="bg-[#252b3b] border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-[#ef4444]" /> Delete Account
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#a0a8b8]">This action is permanent. All your data, calculations, and settings will be permanently deleted.</p>
          <DialogFooter className="gap-2">
            <button onClick={() => setShowDelete(false)} className="h-10 px-5 rounded-xl border border-white/10 text-[#a0a8b8] text-sm hover:bg-white/5 transition-colors" data-testid="cancel-delete-btn">Cancel</button>
            <button onClick={handleDeleteAccount} className="h-10 px-5 rounded-xl bg-[#ef4444] text-white text-sm font-medium btn-lift" data-testid="confirm-delete-btn">Delete Account</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
